import { useCallback, useEffect, useState } from 'react'
import moment from 'moment-timezone'
import useApi from './useApi'

/**
 * hook for user profile data, branches internally: own profile vs other user (by username)
 */
export default function useProfileView({ usernameParam, currentUser }) {
  const {
    getCurrentUser,
    getCurrentUserGroups,
    getUser,
    getUserFollowers,
    getUserGroups,
    getUserEngagementEvents,
  } = useApi()

  const [user, setUser] = useState(null)
  const [groups, setGroups] = useState([])
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [followersCount, setFollowersCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const decodedUsername =
    usernameParam != null ? decodeURIComponent(usernameParam).replace(/^@/, '') : null
  const isOwnProfile =
    currentUser?.nickname != null &&
    decodedUsername != null &&
    decodedUsername === currentUser.nickname

  useEffect(() => {
    if (!decodedUsername) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    setNotFound(false)

    const run = async () => {
      try {
        if (isOwnProfile && currentUser?.id) {
          const [userRes, groupsRes, followersRes, engagementsRes] = await Promise.allSettled([
            getCurrentUser(),
            getCurrentUserGroups(),
            getUserFollowers({ id: currentUser.id }),
            getUserEngagementEvents({ id: currentUser.id }),
          ])
          if (cancelled) return
          if (userRes.status === 'fulfilled' && userRes.value?.data) {
            setUser(userRes.value.data)
          } else if (currentUser) {
            setUser(currentUser)
          }
          if (groupsRes.status === 'fulfilled' && groupsRes.value?.data) {
            setGroups(groupsRes.value.data)
          }
          let count = 0
          if (followersRes.status === 'fulfilled') {
            const data = followersRes.value?.data
            if (Array.isArray(data)) count = data.length
            else if (typeof data?.count === 'number') count = data.count
            else if (Array.isArray(data?.followers)) count = data.followers.length
          }
          if (
            count === 0 &&
            userRes.status === 'fulfilled' &&
            typeof userRes.value?.data?.followers_count === 'number'
          ) {
            count = userRes.value.data.followers_count
          }
          if (!cancelled) setFollowersCount(count)
          if (!cancelled) {
            const tzGuess = moment.tz.guess()
            const startOfToday = moment().tz(tzGuess).startOf('day')

            const engagements =
              engagementsRes.status === 'fulfilled' ? engagementsRes.value?.data ?? [] : []

            // show events today or later
            const filteredUpcoming = (Array.isArray(engagements) ? engagements : []).filter(
              event => {
                if (!event?.event_start_date) return false
                const startMoment = moment(event.event_start_date).tz(tzGuess)
                if (!startMoment.isValid()) return false
                return startMoment.isSameOrAfter(startOfToday)
              },
            )

            const sortedUpcoming = [...filteredUpcoming].sort((a, b) => {
              const aMoment = a?.event_start_date
                ? moment(a.event_start_date).tz(tzGuess)
                : null
              const bMoment = b?.event_start_date
                ? moment(b.event_start_date).tz(tzGuess)
                : null

              const aTime = aMoment?.isValid() ? aMoment.valueOf() : Number.POSITIVE_INFINITY
              const bTime = bMoment?.isValid() ? bMoment.valueOf() : Number.POSITIVE_INFINITY

              return aTime - bTime
            })

            setUpcomingEvents(sortedUpcoming.slice(0, 10))
          }
        } else {
          const userRes = await getUser({ nickname: decodedUsername })
          if (cancelled) return
          if (userRes?.status !== 200 || !userRes?.data) {
            if (!cancelled) setNotFound(true)
            if (!cancelled) setUser(null)
            return
          }
          const profileUser = userRes.data
          if (!cancelled) setUser(profileUser)
          const slugOrId = profileUser.slug ?? profileUser.id
          const [groupsRes, followersRes, engagementsRes] = await Promise.allSettled([
            getUserGroups(slugOrId),
            getUserFollowers({ id: profileUser.id }),
            getUserEngagementEvents({ id: profileUser.id }),
          ])
          if (cancelled) return
          if (groupsRes.status === 'fulfilled' && groupsRes.value?.data) {
            setGroups(groupsRes.value.data)
          }
          let count = 0
          if (followersRes.status === 'fulfilled') {
            const data = followersRes.value?.data
            if (Array.isArray(data)) count = data.length
            else if (typeof data?.count === 'number') count = data.count
            else if (Array.isArray(data?.followers)) count = data.followers.length
          }
          if (count === 0 && typeof profileUser?.followers_count === 'number') {
            count = profileUser.followers_count
          }
          if (!cancelled) {
            setFollowersCount(count)

            const tzGuess = moment.tz.guess()
            const startOfToday = moment().tz(tzGuess).startOf('day')

            const engagements =
              engagementsRes?.status === 'fulfilled' ? engagementsRes.value?.data ?? [] : []

            const filteredUpcoming = (Array.isArray(engagements) ? engagements : []).filter(
              event => {
                if (!event?.event_start_date) return false
                const startMoment = moment(event.event_start_date).tz(tzGuess)
                if (!startMoment.isValid()) return false
                return startMoment.isSameOrAfter(startOfToday)
              },
            )

            const sortedUpcoming = [...filteredUpcoming].sort((a, b) => {
              const aMoment = a?.event_start_date
                ? moment(a.event_start_date).tz(tzGuess)
                : null
              const bMoment = b?.event_start_date
                ? moment(b.event_start_date).tz(tzGuess)
                : null

              const aTime = aMoment?.isValid() ? aMoment.valueOf() : Number.POSITIVE_INFINITY
              const bTime = bMoment?.isValid() ? bMoment.valueOf() : Number.POSITIVE_INFINITY

              return aTime - bTime
            })

            setUpcomingEvents(sortedUpcoming.slice(0, 10))
          }
        }
      } catch (err) {
        if (!cancelled) setNotFound(true)
        if (!cancelled && isOwnProfile && currentUser) setUser(currentUser)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [decodedUsername, isOwnProfile, currentUser?.id, currentUser?.nickname])

  return {
    user,
    setUser,
    groups,
    upcomingEvents,
    followersCount,
    isOwnProfile,
    isLoading,
    notFound,
  }
}
